import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Link,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  PlayCircleOutline as StartIcon,
  RemoveRedEye as ViewCaseIcon
} from '@mui/icons-material';

// Removed Redux auth selectors - now using useAuth() hook
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { surgicalCaseService } from '../../services/surgicalCaseService';
import { ProcedureService } from '../../services/procedureService';
import SurgicalCaseDialog from './SurgicalCaseDialog';
import { useUserData } from '../../hooks/useUserData';

const SurgicalCaseWorkQ = () => {
  const navigate = useNavigate();
  const { user } = useUserData();
  const [surgicalOrders, setSurgicalOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [surgicalCases, setSurgicalCases] = useState({});
  const [caseDialog, setCaseDialog] = useState({ open: false, order: null, surgicalCase: null });
  const [startingCase, setStartingCase] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadSurgicalOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Initialize procedure service inside the callback to avoid dependency issues
      const procedureService = new ProcedureService();

      // Use service layer to fetch surgical orders with patient and encounter data
      const orders = await procedureService.getProceduresByTestType('Surgical', {
        facilityId: user?.facilityId,
        status: statusFilter !== 'all' ? statusFilter : undefined
      });

      // Orders now come with patient and encounter data from the service layer
      setSurgicalOrders(orders);
      
      // Load surgical cases for each order
      await loadSurgicalCases(orders);
    } catch (err) {
      console.error('Error loading surgical orders:', err);
      setError('Failed to load surgical orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.facilityId, statusFilter]);

  useEffect(() => {
    loadSurgicalOrders();
  }, [loadSurgicalOrders]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const loadSurgicalCases = async (orders) => {
    try {
      const casePromises = orders.map(async (order) => {
        const surgicalCase = await surgicalCaseService.getSurgicalCase(order.id);
        return { orderId: order.id, case: surgicalCase };
      });

      const caseResults = await Promise.all(casePromises);
      const casesMap = {};
      caseResults.forEach(({ orderId, case: surgicalCase }) => {
        if (surgicalCase) {
          casesMap[orderId] = surgicalCase;
        }
      });

      setSurgicalCases(casesMap);
    } catch (err) {
      console.error('Error loading surgical cases:', err);
    }
  };

  const handleStartCase = async (order) => {
    try {
      setStartingCase(order.id);
      setError(null);

      const caseId = await surgicalCaseService.startSurgicalCase(
        order.id,
        order,
        user.uid
      );

      // Fetch the created case
      const surgicalCase = await surgicalCaseService.getSurgicalCase(order.id);
      
      // Update the surgical cases state
      setSurgicalCases(prev => ({
        ...prev,
        [order.id]: surgicalCase
      }));

      console.log('Started surgical case:', caseId);
    } catch (err) {
      console.error('Error starting surgical case:', err);
      setError(`Failed to start surgical case: ${err.message}`);
    } finally {
      setStartingCase(null);
    }
  };

  const handleViewCase = (order) => {
    const surgicalCase = surgicalCases[order.id];
    setCaseDialog({
      open: true,
      order: order,
      surgicalCase: surgicalCase
    });
  };

  const handleCloseCaseDialog = () => {
    setCaseDialog({ open: false, order: null, surgicalCase: null });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return format(dateObj, 'MMM dd, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return format(dateObj, 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'ordered': return 'info';
      case 'scheduled': return 'primary';
      case 'in-progress': return 'secondary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': 
      case 'urgent': return 'error';
      case 'medium': 
      case 'normal': return 'warning';
      case 'low': 
      case 'routine': return 'success';
      default: return 'default';
    }
  };

  const handleViewEncounter = (order) => {
    if (order.encounterId) {
      navigate(`/encounters/${order.encounterId}`);
    } else if (order.patientId) {
      navigate(`/patients/${order.patientId}`);
    }
  };

  // Filter orders based on search term and status
  const filteredOrders = surgicalOrders.filter(order => {
    const matchesSearch = !searchTerm || 
      order.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.patientMRN.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.testCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderingProvider?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || 
      order.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading surgical orders...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>


      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              placeholder="Search by patient name, MRN, or provider..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Tooltip title="Refresh">
              <IconButton onClick={loadSurgicalOrders} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Status Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Filter by status:
            </Typography>
            <Chip
              label={`All (${surgicalOrders.length})`}
              onClick={() => {
                setStatusFilter('all');
                setPage(0);
              }}
              variant={statusFilter === 'all' ? 'filled' : 'outlined'}
              color={statusFilter === 'all' ? 'primary' : 'default'}
              sx={{ 
                borderRadius: '20px',
                fontWeight: statusFilter === 'all' ? 600 : 400,
                cursor: 'pointer'
              }}
            />
            <Chip
              label={`Pending (${surgicalOrders.filter(o => o.status?.toLowerCase() === 'pending').length})`}
              onClick={() => {
                setStatusFilter('pending');
                setPage(0);
              }}
              variant={statusFilter === 'pending' ? 'filled' : 'outlined'}
              color={statusFilter === 'pending' ? 'warning' : 'default'}
              sx={{ 
                borderRadius: '20px',
                fontWeight: statusFilter === 'pending' ? 600 : 400,
                cursor: 'pointer'
              }}
            />
            <Chip
              label={`Ordered (${surgicalOrders.filter(o => o.status?.toLowerCase() === 'ordered').length})`}
              onClick={() => {
                setStatusFilter('ordered');
                setPage(0);
              }}
              variant={statusFilter === 'ordered' ? 'filled' : 'outlined'}
              color={statusFilter === 'ordered' ? 'info' : 'default'}
              sx={{ 
                borderRadius: '20px',
                fontWeight: statusFilter === 'ordered' ? 600 : 400,
                cursor: 'pointer'
              }}
            />
            <Chip
              label={`Scheduled (${surgicalOrders.filter(o => o.status?.toLowerCase() === 'scheduled').length})`}
              onClick={() => {
                setStatusFilter('scheduled');
                setPage(0);
              }}
              variant={statusFilter === 'scheduled' ? 'filled' : 'outlined'}
              color={statusFilter === 'scheduled' ? 'secondary' : 'default'}
              sx={{ 
                borderRadius: '20px',
                fontWeight: statusFilter === 'scheduled' ? 600 : 400,
                cursor: 'pointer'
              }}
            />
            <Chip
              label={`In Progress (${surgicalOrders.filter(o => o.status?.toLowerCase() === 'in-progress').length})`}
              onClick={() => {
                setStatusFilter('in-progress');
                setPage(0);
              }}
              variant={statusFilter === 'in-progress' ? 'filled' : 'outlined'}
              color={statusFilter === 'in-progress' ? 'secondary' : 'default'}
              sx={{ 
                borderRadius: '20px',
                fontWeight: statusFilter === 'in-progress' ? 600 : 400,
                cursor: 'pointer'
              }}
            />
            <Chip
              label={`Completed (${surgicalOrders.filter(o => o.status?.toLowerCase() === 'completed').length})`}
              onClick={() => {
                setStatusFilter('completed');
                setPage(0);
              }}
              variant={statusFilter === 'completed' ? 'filled' : 'outlined'}
              color={statusFilter === 'completed' ? 'success' : 'default'}
              sx={{ 
                borderRadius: '20px',
                fontWeight: statusFilter === 'completed' ? 600 : 400,
                cursor: 'pointer'
              }}
            />
            <Chip
              label={`Cancelled (${surgicalOrders.filter(o => o.status?.toLowerCase() === 'cancelled').length})`}
              onClick={() => {
                setStatusFilter('cancelled');
                setPage(0);
              }}
              variant={statusFilter === 'cancelled' ? 'filled' : 'outlined'}
              color={statusFilter === 'cancelled' ? 'error' : 'default'}
              sx={{ 
                borderRadius: '20px',
                fontWeight: statusFilter === 'cancelled' ? 600 : 400,
                cursor: 'pointer'
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Case Actions</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>MRN</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>DOB</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Encounter</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Surgery Details</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Order Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'No surgical orders match your filters' 
                        : 'No surgical orders found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOrders.map((order) => {
                  const hasSurgicalCase = surgicalCases[order.id];
                  const isStarting = startingCase === order.id;
                  
                  return (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        {hasSurgicalCase ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<ViewCaseIcon />}
                            onClick={() => handleViewCase(order)}
                            sx={{ minWidth: 100 }}
                          >
                            View Case
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<StartIcon />}
                            onClick={() => handleStartCase(order)}
                            disabled={isStarting}
                            sx={{ minWidth: 100 }}
                          >
                            {isStarting ? 'Starting...' : 'Start Case'}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.patientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.patientGender}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {order.patientMRN}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(order.patientDOB)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {order.encounterId ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleViewEncounter(order)}
                          sx={{ textDecoration: 'none' }}
                        >
                          {order.encounterFIN}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {order.encounterFIN}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {order.testCode || order.testType || 'Surgical Procedure'}
                        </Typography>
                        {order.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {order.notes.length > 50 ? `${order.notes.substring(0, 50)}...` : order.notes}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {formatDateTime(order.orderDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.orderingProvider || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status || 'Unknown'}
                        color={getStatusColor(order.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.priority || 'Normal'}
                        color={getPriorityColor(order.priority)}
                        size="small"
                        variant="filled"
                        sx={{ opacity: 0.8 }}
                      />
                    </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Encounter">
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewEncounter(order)}
                              disabled={!order.encounterId && !order.patientId}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
      </Paper>

      {/* Surgical Case Dialog */}
      <SurgicalCaseDialog
        open={caseDialog.open}
        onClose={handleCloseCaseDialog}
        order={caseDialog.order}
        surgicalCase={caseDialog.surgicalCase}
      />
    </Box>
  );
};

export default SurgicalCaseWorkQ; 